import {
  buildOrderedPublicProviderListRows,
  EnhancedSeoHead,
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateItemListSchema,
  NotFoundPage,
  ProviderList,
  publicListSeoState,
} from "@appcaire/shared/seo";
import { ArrowRight, Building2, ChevronDown, Info, MapPin } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ConversionCtaBand } from "../components/ConversionCtaBand";
import { useStockholmEventTracking } from "../lib/analytics";
import { buildDistrictFaqItems } from "../lib/districtFaq";
import { stockholmProviderCanonicalUrl } from "../lib/env";
import { STOCKHOLM_LIST_COPY } from "../lib/list-copy";
import {
  districtSlug,
  formatSourceDate,
  providerDistricts,
  useStockholmMunicipality,
  useStockholmProviders,
} from "../lib/providers";
import { listPageHref, pageSuffix } from "../lib/seo-text";
import { OFFICIAL_STOCKHOLM_CHOOSER_URL, SITE_ORIGIN } from "../lib/site";
import { ShortlistButton } from "../shortlist/ShortlistButton";
import { DataUnavailable, PageLoading } from "./PageStates";

function mean(values: Array<number | null | undefined>): number | null {
  const valid = values.filter(
    (value): value is number =>
      typeof value === "number" && Number.isFinite(value),
  );
  return valid.length
    ? valid.reduce((sum, value) => sum + value, 0) / valid.length
    : null;
}

const DEFAULT_PAGE_SIZE = 25;
const CAIRE_INDEX_METHODOLOGY_URL =
  "https://sverigeshemtjanst.se/topplistor/metodik";
const buildStockholmProviderHref = (slug: string) =>
  `/utforare/${encodeURIComponent(slug)}`;

const districtSeoState = (
  searchParams: URLSearchParams,
  canonicalPath: string,
  maxPage: number,
) => publicListSeoState(searchParams, SITE_ORIGIN, canonicalPath, { maxPage });

export function DistrictIndexPage() {
  const query = useStockholmProviders();
  if (query.loading) return <PageLoading label="Hämtar stadsdelar…" />;
  if (query.error || query.providers.length === 0)
    return <DataUnavailable path="/stadsdelar" />;

  return (
    <>
      <EnhancedSeoHead
        siteKey="stockholm"
        title="Hemtjänst per stadsdel i Stockholm"
        description={`Hitta hemtjänst i din stadsdel i Stockholm. Se vilka utförare som arbetar i stadens ${query.districts.length} stadsdelar och välj den som passar dig.`}
        canonicalUrl={`${SITE_ORIGIN}/stadsdelar`}
        keywords={[
          "hemtjänst stadsdel Stockholm",
          "hemtjänst Kungsholmen",
          "hemtjänst Norrmalm",
          "hemtjänst Älvsjö",
        ]}
        contentClassification="Local consumer directory"
        targetAudience="Stockholm residents, seniors, relatives"
        schemaMarkup={generateBreadcrumbSchema([
          { name: "Hem", url: SITE_ORIGIN },
          { name: "Stadsdelar", url: `${SITE_ORIGIN}/stadsdelar` },
        ])}
      />
      <header className="border-b border-border bg-accent/65">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-secondary">
            <MapPin className="h-4 w-4" aria-hidden="true" /> Hemtjänst nära dig
          </p>
          <h1 className="mt-3 font-[var(--font-editorial)] text-4xl font-bold text-foreground md:text-5xl">
            Hemtjänst i Stockholms stadsdelar
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-muted-foreground">
            Välj din stadsdel så ser du vilka hemtjänstutförare som arbetar där.
            Många utförare arbetar i flera stadsdelar.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Uppgifterna kommer från Stockholms stad, uppdaterade{" "}
            {formatSourceDate(query.sourceDate)}.
          </p>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {query.districts.map((district) => (
            <Link
              key={district.slug}
              to={`/stadsdelar/${district.slug}`}
              className="group rounded-2xl border border-border bg-card p-6 shadow-sm transition hover:-translate-y-1 hover:border-brand-primary/35 hover:shadow-lg motion-reduce:transform-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-brand-primary">
                <Building2 className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="mt-5 text-xl font-bold text-foreground">
                Hemtjänst {district.name}
              </h2>
              <p className="mt-2 text-muted-foreground">
                {district.count} utförare arbetar i stadsdelen.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 font-semibold text-brand-primary">
                Visa listan{" "}
                <ArrowRight
                  className="h-4 w-4 transition group-hover:translate-x-1 motion-reduce:transform-none"
                  aria-hidden="true"
                />
              </span>
            </Link>
          ))}
        </div>
        <ConversionCtaBand sourceSurface="district" />
      </div>
    </>
  );
}

export function DistrictPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const providerQuery = useStockholmProviders();
  const municipalityQuery = useStockholmMunicipality();
  const [searchParams] = useSearchParams();
  const trackStockholmEvent = useStockholmEventTracking();
  if (providerQuery.loading || municipalityQuery.loading)
    return <PageLoading label="Hämtar utförare i stadsdelen…" />;
  if (providerQuery.error || municipalityQuery.error)
    return <DataUnavailable path={`/stadsdelar/${slug}`} />;

  const district = providerQuery.districts.find((item) => item.slug === slug);
  if (!district) {
    return (
      <NotFoundPage
        title="Vi har inga utförare för den här stadsdelen"
        description="Vi visar bara stadsdelar där Stockholms stad har utförare registrerade. Prova en annan stadsdel eller sök i hela listan."
        navigationLinks={[
          { label: "Alla stadsdelar", to: "/stadsdelar" },
          { label: "Hitta hemtjänst", to: "/hitta-hemtjanst" },
          { label: "Startsidan", to: "/" },
        ]}
      />
    );
  }

  const providers = providerQuery.providers.filter((provider) =>
    providerDistricts(provider).some(
      (area) => districtSlug(area) === district.slug,
    ),
  );
  const orderedRows = buildOrderedPublicProviderListRows(providers, {
    municipalitySlug: "stockholm",
    municipalityHref: `/stadsdelar/${district.slug}`,
    providerHrefBuilder: buildStockholmProviderHref,
    groupHrefBuilder: false,
  });
  const maximumPage = Math.max(
    1,
    Math.ceil(orderedRows.length / DEFAULT_PAGE_SIZE),
  );
  const { page, cleanPagination, indexable, canonicalUrl } = districtSeoState(
    searchParams,
    `/stadsdelar/${district.slug}`,
    maximumPage,
  );
  if (cleanPagination && page > maximumPage) {
    return (
      <NotFoundPage
        title="Sidan finns inte i stadsdelslistan"
        description={`Listan för ${district.name} har ${maximumPage} sidor. Välj en befintlig sida eller börja om från första sidan.`}
        navigationLinks={[
          {
            label: `Första sidan för ${district.name}`,
            to: `/stadsdelar/${district.slug}`,
          },
          { label: "Alla stadsdelar", to: "/stadsdelar" },
          { label: "Hitta hemtjänst", to: "/hitta-hemtjanst" },
        ]}
      />
    );
  }
  const municipality = municipalityQuery.data?.municipality;
  const listSource = {
    __typename: "MunicipalProviderListSource" as const,
    tier: municipality?.providerListSource?.tier ?? "live",
    url:
      municipality?.providerListSource?.url ?? OFFICIAL_STOCKHOLM_CHOOSER_URL,
  };
  const pageRows = orderedRows.slice(
    (page - 1) * DEFAULT_PAGE_SIZE,
    page * DEFAULT_PAGE_SIZE,
  );
  const districtFaqs = buildDistrictFaqItems(district.name);
  const schemaMarkup = [
    generateBreadcrumbSchema([
      { name: "Hem", url: SITE_ORIGIN },
      { name: "Stadsdelar", url: `${SITE_ORIGIN}/stadsdelar` },
      {
        name: district.name,
        url: `${SITE_ORIGIN}/stadsdelar/${district.slug}`,
      },
    ]),
    ...(indexable
      ? [
          generateItemListSchema({
            name: `Hemtjänstutförare i ${district.name}`,
            url: canonicalUrl,
            items: pageRows.map((row, index) => ({
              name: row.name,
              url: stockholmProviderCanonicalUrl(row.slug),
              position: (page - 1) * DEFAULT_PAGE_SIZE + index + 1,
            })),
          }),
          generateFAQSchema(districtFaqs, canonicalUrl),
        ]
      : []),
  ];

  return (
    <>
      <EnhancedSeoHead
        siteKey="stockholm"
        title={`Välj hemtjänst i ${district.name} – jämför utförare${page > 1 ? ` – sida ${page}` : ""}`}
        description={`Hitta hemtjänstutförare i ${district.name}, Stockholm. Jämför kontakt, kommunal eller privat, språk och kvalitet från officiella källor.${pageSuffix(page, maximumPage)}`}
        canonicalUrl={canonicalUrl}
        indexable={indexable}
        keywords={[
          `hemtjänst ${district.name}`,
          `hemtjänstutförare ${district.name}`,
          `välja hemtjänst ${district.name}`,
        ]}
        contentClassification="Local consumer provider directory"
        targetAudience={`residents and relatives in ${district.name}, Stockholm`}
        schemaMarkup={schemaMarkup}
        entities={[
          { type: "Place", name: district.name },
          { type: "Service", name: "Hemtjänst" },
        ]}
      />
      <header className="border-b border-border bg-accent/65">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <nav
            aria-label="Brödsmulor"
            className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
          >
            <Link to="/" className="hover:text-brand-primary hover:underline">
              Hem
            </Link>
            <span aria-hidden="true">/</span>
            <Link
              to="/stadsdelar"
              className="hover:text-brand-primary hover:underline"
            >
              Stadsdelar
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-foreground">{district.name}</span>
          </nav>
          <h1 className="mt-3 font-[var(--font-editorial)] text-4xl font-bold text-foreground md:text-5xl">
            Välj hemtjänst i {district.name}
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-muted-foreground">
            {orderedRows.length} publikt listade utförare i {district.name} i
            vårt aktuella register. Se listan nedan och kontrollera alltid
            aktuella val hos kommunen.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Uppgifterna kommer från Stockholms stad, uppdaterade{" "}
            {formatSourceDate(providerQuery.sourceDate)}.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <aside className="mb-8 flex gap-3 rounded-2xl border border-brand-primary/20 bg-brand-primary/5 p-5 text-sm leading-6 text-muted-foreground">
          <Info
            className="mt-0.5 h-5 w-5 shrink-0 text-brand-primary"
            aria-hidden="true"
          />
          <p>
            Att en utförare finns med i stadsdelen betyder inte att de kan ta
            emot dig just nu. Fråga utföraren om de kan börja hemma hos dig och
            vid de tider du behöver.
          </p>
        </aside>
        <ProviderList
          providers={providers}
          stats={{
            totalProviders: orderedRows.length,
            filteredCount: orderedRows.length,
            avgSatisfaction: mean(orderedRows.map((row) => row.satisfaction)),
            avgContinuity: mean(orderedRows.map((row) => row.continuity)),
          }}
          municipalitySlug="stockholm"
          copy={STOCKHOLM_LIST_COPY}
          municipalityHref={`/stadsdelar/${district.slug}`}
          providerHrefBuilder={buildStockholmProviderHref}
          groupHrefBuilder={false}
          listSource={listSource}
          presentation="consumer"
          showCaireIndex
          ranks={{
            municipality: { label: "Stockholms stad" },
            national: { label: "Nationellt" },
          }}
          caireIndexMethodologyHref={CAIRE_INDEX_METHODOLOGY_URL}
          renderProviderActions={(row) => <ShortlistButton row={row} />}
          paginationHrefBuilder={(nextPage, pageSize) =>
            listPageHref(
              `/stadsdelar/${district.slug}`,
              searchParams,
              nextPage,
              pageSize,
            )
          }
        />
        <section className="mt-10" aria-labelledby="district-faq-heading">
          <h2
            id="district-faq-heading"
            className="text-2xl font-bold text-foreground"
          >
            Vanliga frågor om hemtjänst i {district.name}
          </h2>
          <div className="mt-5 space-y-3">
            {districtFaqs.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-2xl border border-border bg-card p-5 shadow-sm"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold text-foreground">
                  {faq.question}
                  <ChevronDown
                    className="h-5 w-5 shrink-0 text-brand-primary transition group-open:rotate-180 motion-reduce:transition-none"
                    aria-hidden="true"
                  />
                </summary>
                <p className="mt-3 text-lg leading-8 text-muted-foreground">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </section>

        <nav
          className="mt-10 grid gap-4 sm:grid-cols-3"
          aria-label="Relaterad vägledning"
        >
          <Link
            to="/guider/valja-hemtjanst"
            onClick={() =>
              trackStockholmEvent("seo_funnel_click", {
                source_surface: "district",
                target_path: "/guider/valja-hemtjanst",
              })
            }
            className="rounded-2xl border border-border bg-card p-5 font-semibold text-brand-primary hover:border-brand-primary/40"
          >
            Så väljer du hemtjänstutförare i Stockholm
          </Link>
          <Link
            to="/guider/checklista-hemtjanstutforare"
            onClick={() =>
              trackStockholmEvent("seo_funnel_click", {
                source_surface: "district",
                target_path: "/guider/checklista-hemtjanstutforare",
              })
            }
            className="rounded-2xl border border-border bg-card p-5 font-semibold text-brand-primary hover:border-brand-primary/40"
          >
            Checklista innan du kontaktar utförare
          </Link>
          <Link
            to="/guider/ansoka-hemtjanst-stockholm"
            onClick={() =>
              trackStockholmEvent("seo_funnel_click", {
                source_surface: "district",
                target_path: "/guider/ansoka-hemtjanst-stockholm",
              })
            }
            className="rounded-2xl border border-border bg-card p-5 font-semibold text-brand-primary hover:border-brand-primary/40"
          >
            Ansöka om hemtjänst i Stockholm
          </Link>
        </nav>
        <ConversionCtaBand
          sourceSurface="district"
          title={`Kontakta utförare i ${district.name}`}
          description="Öppna en utförare i listan ovan och skicka en förfrågan, eller kontakta Caire om du har frågor om guiden."
        />
      </div>
    </>
  );
}
